import jsPDF from 'jspdf';
import PdfBuilder from '../renderers/pdf/PdfBuilder';
import { renderAasEnvironment } from '../renderers/pdf/AasPdfRenderer';

export const generatePdf = async (env, aasIdBase64, locale, setError, setSuccess) => {
  try {
    const doc = new jsPDF({
      orientation: 'p',
      unit: 'mm',
      format: 'a4',
      putOnlyUsedFonts: true,
    });

    const builder = new PdfBuilder(doc, locale || 'de');
    renderAasEnvironment(builder, env);

    doc.save(`aas_environment_${aasIdBase64}.pdf`);
    setSuccess('Aktion erfolgreich');

  } catch (err) {
    console.error(err);
    setError('Failed to generate PDF.');
  }
};

/* // Utility functions (unchanged)
const getLocalizedText = (value, fallback, locale) => {
  if (typeof value === 'string') {
    return value || fallback;
  }
  if (!Array.isArray(value) || value.length === 0) {
    return fallback;
  }
  const currentLocale = (locale || 'en').toLowerCase();
  const matchedItem = value.find(item => item?.language?.toLowerCase() === currentLocale);
  const itemToUse = matchedItem || value[0];
  return itemToUse?.text ? itemToUse.text.trim() : fallback;
};

const renderValue = (value, fallback = 'N/A', locale) => {
  if (Array.isArray(value) && value.length > 0) {
    const currentLocale = (locale || 'en').toLowerCase();
    const matchedItem = value.find(item => item?.language?.toLowerCase() === currentLocale);
    const itemToUse = matchedItem || value[0];
    return itemToUse?.text ? itemToUse.text.trim() : fallback;
  } else if (typeof value === 'string') {
    return value;
  }
  return fallback;
};

// Async function to generate PDF with styling
export const generatePdf = async (aasId, aasIdBase64, setError, setSuccess, locale) => {
  try {
    const serializedData = await fetchSerializedAasEnvironment(aasId);
    const doc = new jsPDF({
      orientation: 'p',
      unit: 'mm',
      format: 'a4',
      putOnlyUsedFonts: true,
    });
    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;
    const margin = 10;
    const maxWidthLeft = 100;
    const maxWidthRight = 100;
    const indentStep = 5;
    const lineHeight = 8;
    const headerHeight = 15; // Height of the blue header
    let yPosition = margin + headerHeight; // Shift initial content down by header height

    // Colors
    const primaryColor = '#003E81'; // Updated to exact color
    const secondaryColor = '#FFFFFF';
    const textColor = '#000000';
    const accentColor = '#4B5EAA';

    const brandImage = require('../../assets/brand_small.jpg');

    // Function to add header and footer on each page
    const addHeaderFooter = () => {
      // Header
      doc.setFillColor(primaryColor);
      doc.rect(0, 0, pageWidth, headerHeight, 'F'); // Blue header
      // Add brand image
      try {
        doc.addImage(brandImage, 'PNG', margin, 2, 22, 10);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor('#FFFFFF');
        doc.text('Digital Product Passports', margin + 32, 9);
      } catch (imgError) {
        console.error('Failed to add brand image:', imgError);
        // Fallback text if image fails
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor('#FFFFFF');
        doc.text('Brand Logo', margin, 10);
      }
      // Footer
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(textColor);
      doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
    };

    // Function to check page overflow and add header/footer
    const checkPageOverflow = (additionalHeight) => {
      if (yPosition + additionalHeight > pageHeight - margin) {
        doc.addPage();
        yPosition = margin + headerHeight; // Reset yPosition with header offset
        addHeaderFooter();
      }
    };

    // Set initial header/footer
    addHeaderFooter();

    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(primaryColor);
    checkPageOverflow(lineHeight * 2);
    doc.text('Product Passport', margin, yPosition);
    yPosition += lineHeight * 2;

    // Asset Information Section
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(accentColor);
    checkPageOverflow(lineHeight);
    doc.setFillColor(secondaryColor);
    doc.rect(margin, yPosition - 5, pageWidth - 2 * margin, lineHeight + 2, 'F');
    doc.text('Asset Information', margin, yPosition);
    yPosition += lineHeight;

    const assetInfo = serializedData.assetAdministrationShells?.[0]?.assetInformation || {};
    const globalAssetId = assetInfo.globalAssetId || 'N/A';
    const assetKind = assetInfo.assetKind || 'N/A';

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(textColor);
    checkPageOverflow(lineHeight * 2);
    doc.text(`Global Asset ID: ${globalAssetId}`, margin, yPosition, { maxWidth: maxWidthLeft + maxWidthRight });
    yPosition += lineHeight;
    doc.text(`Asset Kind: ${assetKind}`, margin, yPosition, { maxWidth: maxWidthLeft + maxWidthRight });
    yPosition += lineHeight * 2;

    const submodels = serializedData.submodels || [];

    const renderElement = (element, indentLevel = 1) => {
      const { idShort, displayName, value, modelType, submodelElements } = element || {};
      const indent = indentLevel === 1 ? margin : margin + (indentLevel - 1) * indentStep;
      const displayText = getLocalizedText(displayName, idShort || 'N/A', locale);

      let renderedValue = 'N/A';
      if (modelType === 'MultiLanguageProperty') {
        renderedValue = renderValue(value, 'N/A', locale);
      } else if (value && typeof value === 'string') {
        renderedValue = value;
      }

      if (modelType !== 'SubmodelElementCollection' && modelType !== 'SubmodelElementList') {
        checkPageOverflow(lineHeight);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(textColor);
        doc.text(displayText, indent, yPosition, { maxWidth: maxWidthLeft });
        doc.text(renderedValue, pageWidth - margin, yPosition, {
          maxWidth: maxWidthRight,
          align: 'right',
        });
        yPosition += lineHeight;
      }

      if ((modelType === 'SubmodelElementCollection' || modelType === 'SubmodelElementList') && submodelElements) {
        checkPageOverflow(lineHeight);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(accentColor);
        doc.setFillColor(secondaryColor);
        doc.rect(indent, yPosition - 5, pageWidth - indent - margin, lineHeight + 2, 'F');
        doc.text(displayText, indent, yPosition, { maxWidth: maxWidthLeft });
        yPosition += lineHeight;

        submodelElements.forEach((subElement) => {
          renderElement(subElement, indentLevel + 1);
        });
      }

      if (Array.isArray(value) && modelType !== 'MultiLanguageProperty') {
        value.forEach((item) => {
          renderElement(item, indentLevel + 1);
        });
      }
    };

    submodels.forEach((submodel, index) => {
      if (index > 0 || yPosition > margin + headerHeight) {
        doc.addPage();
        yPosition = margin + headerHeight; // Reset with header offset
        addHeaderFooter();
      }

      const { idShort, displayName, description, submodelElements } = submodel || {};
      const displayNameToShow = getLocalizedText(displayName, idShort || 'N/A', locale);
      const descriptionText = getLocalizedText(description, 'No description available', locale);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(accentColor);
      doc.setFillColor(secondaryColor);
      doc.rect(margin, yPosition - 5, pageWidth - 2 * margin, lineHeight + 2, 'F');
      doc.text(`${displayNameToShow}`, margin, yPosition);
      yPosition += lineHeight;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(textColor);
      doc.text(descriptionText, margin, yPosition, { maxWidth: pageWidth - 2 * margin });
      yPosition += lineHeight * 2;

      if (Array.isArray(submodelElements)) {
        submodelElements.forEach((element) => {
          renderElement(element, 1);
        });
      } else {
        checkPageOverflow(lineHeight);
        doc.text('No submodel elements available', margin, yPosition);
        yPosition += lineHeight;
      }

      yPosition += lineHeight;
    });

    doc.save(`aas_environment_${aasIdBase64}.pdf`);
    setSuccess('Aktion erfolgreich');
  } catch (err) {
    console.error('PDF download failed:', err);
    setError(err.message || 'Failed to download serialized AAS environment.');
  }
};

// Component (unchanged)
const PdfDownloadHandler = ({ data, aasId, aasIdBase64, setError, setSuccess }) => {
  const intl = useIntl();
  const currentLocale = (intl.locale || 'en').toLowerCase();

  if (data?.format === 'pdf') {
    generatePdf(aasId, aasIdBase64, setError, setSuccess, currentLocale);
  }

  return null;
};
 */
//export default PdfDownloadHandler;